/*
  Показ денег, дат и сроков.

  Суммы приходят с сервера строками, а не числами: разбор JSON превращает
  число в двоичную дробь, и на длинных суммах теряются копейки. Здесь строка
  форматируется для показа и **никогда не участвует в вычислениях** — считает
  сервер, экран только показывает.
*/

const MONEY = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const MONEY_SHORT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })

export function money(value, { short = false } = {}) {
  if (value === null || value === undefined) return 'нет данных'
  const asNumber = Number(value)
  if (Number.isNaN(asNumber)) return 'нет данных'
  // Неразрывный пробел перед знаком рубля: сумма не должна переноситься
  // по строке отдельно от него.
  return `${short ? MONEY_SHORT.format(asNumber) : MONEY.format(asNumber)}\u00A0₽`
}

export function date(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleDateString('ru-RU')
}

export function dateTime(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/*
  Отчётный месяц. Значение везде - строка «ГГГГ-ММ», ровно та, что уходит
  на сервер: второго представления месяца в кабинете нет, иначе выбранное на
  экране и запрошенное у сервера рано или поздно разъедутся.

  Название месяца берётся у браузера, а не заводится списком: свой список
  названий стал бы вторым определением того, что и так есть в системе.
*/

/** Месяц строкой «ГГГГ-ММ» из объекта даты. */
export function monthKey(when) {
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}`
}

/** Название месяца для человека: «2026-09» - «сентябрь 2026». */
export function monthTitle(key) {
  const [year, month] = key.split('-').map(Number)
  const named = new Date(year, month - 1, 1).toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
  })
  // Браузер добавляет «г.» в конце - в подписи выбора он лишний.
  return named.replace(/\s*г\.$/, '')
}

/**
  Месяц начисления коротко: «2024-10-01» - «окт. 2024». Для подписей, где полное
  название не помещается, - надписи о периоде долга в углу карточки счёта
  (05.10.2026). Название, как и у `monthTitle`, даёт браузер.
*/
export function monthShort(period) {
  const [year, month] = String(period).split('-').map(Number)
  const named = new Date(year, month - 1, 1).toLocaleDateString('ru-RU', {
    month: 'short',
    year: 'numeric',
  })
  return named.replace(/\s*г\.$/, '')
}

/**
  Месяц начисления так, как его пишут выгрузки и наш шаблон для УК: «2026-03-01» -
  «03.2026». Человек ищет строку в своём файле именно по этой записи (01.10.2026,
  месяцы из двух источников).
*/
export function chargeMonth(period) {
  const [year, month] = String(period).split('-')
  return `${month}.${year}`
}

/** Предыдущий месяц - отчётный по умолчанию: текущий ещё не закрыт. */
export function previousMonth(when = new Date()) {
  return monthKey(new Date(when.getFullYear(), when.getMonth() - 1, 1))
}

/** Последние месяцы для выбора, свежие сверху: { '2026-09': 'сентябрь 2026' }. */
export function lastMonths(count, when = new Date()) {
  const options = {}
  for (let back = 0; back < count; back += 1) {
    const key = monthKey(new Date(when.getFullYear(), when.getMonth() - back, 1))
    options[key] = monthTitle(key)
  }
  return options
}

/*
  Периоды счётчиков окупаемости (08.09.2026, замечание владельца: «хочется
  выбирать период»). Считаются от даты расчёта на клиенте - сервер принимает
  готовую дату начала (`since`), а не название периода: сегодняшний «квартал»
  через год означал бы другой отрезок, и повторить старую цифру было бы нечем.

  «За всё время» приходит с сервера полем `history_from` - самая ранняя запись,
  которую считает отчёт (первая загрузка, смена стадии, платёж после нашего
  действия). Не день первой загрузки: платежи и стадии бывают датированы раньше
  неё, и «всё время» выходило уже, чем «с начала года» (27.10.2026). Круглая дата
  посчитала бы эффект за месяцы, когда платформы у клиента ещё не было.
*/
export const PERIODS = {
  year: 'с начала года',
  months12: 'за последние 12 месяцев',
  quarter: 'за последний квартал',
  month: 'за последний месяц',
  all: 'за всё время',
}

/** Сегодняшний день в виде «ГГГГ-ММ-ДД» - тот же вид, что принимает сервер. */
export function today(when = new Date()) {
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}-${String(
    when.getDate(),
  ).padStart(2, '0')}`
}

/** Дата начала выбранного периода в виде «ГГГГ-ММ-ДД». */
export function periodStart(period, { historyFrom = null, now = new Date() } = {}) {
  if (period === 'all') return historyFrom
  if (period === 'year') return today(new Date(now.getFullYear(), 0, 1))
  const back = { months12: 12, quarter: 3, month: 1 }[period]
  /*
    День берётся не больше последнего дня целевого месяца (аудит 28.10.2026, раздел 5.4). Раньше
    `new Date(год, месяц - 1, 31)` для месяца без 31-го числа переносилось на следующий: 31 марта
    минус месяц давало 3 марта, и февраль выпадал из периода. Так же ломались квартал от 31 мая
    и год от 29 февраля.
  */
  const lastDay = new Date(now.getFullYear(), now.getMonth() - back + 1, 0).getDate()
  return today(new Date(now.getFullYear(), now.getMonth() - back, Math.min(now.getDate(), lastDay)))
}

export function percent(share) {
  if (share === null || share === undefined) return 'считать не от чего'
  return `${(Number(share) * 100).toFixed(1).replace('.', ',')}%`
}

/** Число случаев, по которым посчитана доля: 1 случай, 2 случая, 5 случаев. */
export const cases = (count) => plural(count, 'случай', 'случая', 'случаев')

/*
  Разряды у количеств разделяются так же, как у сумм (17.09.2026, замечание
  владельца). До этого дня «9516 строк» стояло рядом с «7 625 753,78 ₽» в одной
  сводке: суммы шли через `Intl`, а количества склеивались в строку как есть.
  Одно правило на все числа экрана - иначе читатель спотыкается о разнобой там,
  где величины стоят рядом.

  Неразрывный пробел `Intl` даёт сам: число не должно переноситься по строке.
*/
const COUNT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })

/** Склонение существительного при числе: 1 счёт, 2 счёта, 5 счетов. */
export function plural(count, one, few, many) {
  const mod10 = count % 10
  const mod100 = count % 100
  const shown = COUNT.format(count)
  if (mod10 === 1 && mod100 !== 11) return `${shown} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${shown} ${few}`
  return `${shown} ${many}`
}

/** Число с разрядами, как у сумм: «37 397» (аудит 28.10.2026: «принято строк: 37397» без них). */
export const grouped = (value) => COUNT.format(value)

export const accounts = (count) => plural(count, 'счёт', 'счёта', 'счетов')
/*
  Счета в дательном падеже: «по 1 счёту», «по 2 счетам», «по 5 счетам».

  Заведено 11.09.2026: строка дашборда «Горит срок давности: 42 700,00 ₽ по
  2 счёта» склоняла число именительным падежом посреди предложения, которое
  требует дательного. Падеж - часть подписи, и выбирается там же, где сама
  подпись: собирать его в разметке значило бы держать половину фразы в одном
  файле, половину в другом.
*/
export const accountsDative = (count) => plural(count, 'счёту', 'счетам', 'счетам')
/* Родительный: «стадия сменилась у 1 счёта», «у 2 счетов» (16.10.2026) - по той
   же причине, что дательный выше. */
export const accountsGenitive = (count) => plural(count, 'счёта', 'счетов', 'счетов')
/* Предложный: «в 1 счёте», «в 3 счетах» (20.10.2026, строка о расхождении ФИО). */
export const accountsPrepositional = (count) => plural(count, 'счёте', 'счетах', 'счетах')
/* Родительный для «из N»: «1 из 1 случая», «из 1 документа», «из 1 строки» (аудит 28.10.2026). */
export const casesGenitive = (count) => plural(count, 'случая', 'случаев', 'случаев')
export const documentsGenitive = (count) => plural(count, 'документа', 'документов', 'документов')
export const rowsGenitive = (count) => plural(count, 'строки', 'строк', 'строк')
export const rows = (count) => plural(count, 'строка', 'строки', 'строк')
export const columns = (count) => plural(count, 'колонка', 'колонки', 'колонок')
export const contacts = (count) => plural(count, 'обращение', 'обращения', 'обращений')
export const days = (count) => plural(count, 'день', 'дня', 'дней')
export const documents = (count) => plural(count, 'документ', 'документа', 'документов')
export const claims = (count) => plural(count, 'требование', 'требования', 'требований')

/*
  Примечание о пошлине на плитке «К взысканию».

  Показывается та пошлина, которую придётся уплатить: требование дороже предела
  приказного производства взыскивается только иском, и она вдвое больше
  (16.09.2026, строка 73 реестра). Подпись меняется целиком - «Исковая
  пошлина», - а не дописывается второй строкой: примечания плиток держат один
  ряд, и лишняя строка сломала бы его. Почему счёт пошёл иском, сказано ниже в
  списке причин.

  Собрано здесь, а не в разметке, по той же причине, что и падеж счетов выше:
  фраза, половина которой лежит в одном файле, а половина в другом, расходится
  при первой же правке. Прежняя разметка приписывала «я» к слову «требование»
  и на двух требованиях давала «требованиея».
*/
export function feeNote(verdict) {
  // Вердикт «неизвестно» обнуляет пошлину и число требований (гейт не считает без тарифов
  // или состава), и «0,00 ₽ за 0 требований» читалось как расчёт (аудит 26.10.2026,
  // расхождение 12). Неизвестное называется неизвестным.
  if (verdict.band === 'unknown') return 'Пошлина пока не посчитана'
  // Приказ отменён и за него внесена пошлина (строка 289, 28.10.2026): к уплате доплата, и
  // подпись называет обе суммы - иначе «пошлина 2 750» рядом с исковой 5 500 в блоке иска
  // читалась бы как расхождение.
  if (Number(verdict.fee_offset ?? 0) > 0) {
    return `Доплата по иску ${money(verdict.filing_fee_total)}, зачтено за приказ ${money(verdict.fee_offset)}`
  }
  const filing = Number(verdict.filing_fee_total)
  const order = Number(verdict.state_fee_total)
  const label = filing > order ? 'Исковая пошлина' : 'Пошлина'
  return `${label} ${money(verdict.filing_fee_total)} за ${claims(verdict.claims)}`
}

export const ROUTE_TITLES = {
  court: 'в суд',
  lawsuit: 'только исковое',
  pretrial_batch: 'досудебная рассылка',
  excluded: 'исключён',
  manual_review: 'нужен человек',
  nothing_to_do: 'в норме',
}

export const LIMITATION_TITLES = {
  within: 'в пределах срока',
  expiring_soon: 'горит давность',
  expired: 'срок истёк',
}

export const ZONE_TITLES = {
  normal: 'в норме',
  debtor_work: 'работа с должником',
  court: 'суд и исполнение',
}

export const AMOUNT_TITLES = {
  up_to_10k: 'до 10 тыс',
  from_10k: '10–100 тыс',
  from_100k: '100–300 тыс',
  over_300k: 'свыше 300 тыс',
}

export const CONTACT_TITLES = {
  contactable: 'есть связь',
  unreachable: 'нет связи',
}

/*
  Две оси отбора, заведённые 29.09.2026 вместе со ссылками с плашек дашборда.

  Плашки «В работе» и «Сгорело по давности» считают множества, которых в отборе
  реестра не было: «стадия вышла из зоны в норме» и «часть долга уже выпала за
  срок». Без своих осей ссылка с плашки привела бы в реестр, показывающий не те
  счета, - а число на плашке и число найденного обязаны совпадать.

  «Сгоревшая часть» - НЕ то же, что «Давность: срок истёк»: та полоса означает,
  что в пределах срока не осталось ничего, а здесь довольно одного выпавшего
  начисления.
*/
export const WORK_TITLES = {
  in_progress: 'идёт',
  not_in_progress: 'не идёт',
}

export const EXPIRED_TITLES = {
  some: 'есть',
  none: 'нет',
}

// Что должник заявил о себе - в разговоре или на своей странице (§3.5, §3.8).
// Каждая причина ведёт в свою ветку и по-разному меняет работу по счёту,
// поэтому в карточке она названа словами, а не кодом.
export const PAUSE_REASON_TITLES = {
  hardship: 'тяжёлая жизненная ситуация',
  disputed_amount: 'не согласен с суммой',
  claims_paid: 'говорит, что уже оплатил',
  not_the_debtor: 'говорит, что счёт не его',
}

// Чем человек отвечает по счёту. При долевой ответственности по одному счёту
// подаётся столько заявлений, сколько сособственников, и столько же пошлин
// (§6.9) - поэтому вид ответственности стоит рядом с именем, а не в глубине
// карточки.
export const LIABILITY_TITLES = {
  solidary: 'солидарная',
  shared: 'долевая',
}

export const PERSON_ROLE_TITLES = {
  owner: 'собственник',
  tenant: 'наниматель',
  family_member: 'член семьи',
  heir: 'наследник',
  guardian: 'законный представитель',
}

// Способ передачи ссылки задаёт её срок жизни: продиктованную по телефону
// открывают позже всего, выданную на приёме - в тот же день (§3.8.2).
export const LINK_PURPOSE_TITLES = {
  operator: 'выдам сам - на приёме или в разговоре (7 дней)',
  message: 'отправлю сообщением (14 дней)',
  voice: 'продиктую по телефону (30 дней)',
}

// События исполнительного производства и то, куда предъявлен документ. Названия
// человеческие: «partial_payment» в выпадающем списке кабинета читает не
// программист, а юрист организации (§4.5.1).
export const ENFORCEMENT_EVENT_TITLES = {
  presented: 'предъявлен к исполнению',
  partial_payment: 'частичное удержание',
  returned_by_creditor: 'возвращён по нашему заявлению',
  ended_no_property: 'окончено: должник или имущество не найдены',
  ended_paid: 'исполнено полностью',
  cancelled: 'приказ отменён',
}

/*
  🔴 Виды события, которые человек отмечает РУКАМИ у карточки документа. Отмена
  приказа сюда не входит (14.09.2026): она меняет не только судьбу документа, но
  и стадию счёта - он уходит в исковое производство, - и потому отмечается одним
  действием на карточке счёта, блоком «Приказ отменён». Выбранная здесь, она
  вычеркнула бы приказ из реестра, оставив счёт стоять в судебной стадии.

  Справочник названий при этом обязан знать ВСЕ значения перечисления сервера -
  это стережёт гейт `test_titles_match_enums.py`, и он прав: подпись нужна везде,
  где вид события показывается, а не только там, где его выбирают. Поэтому
  словарь полный, а перечень для выбора - производный от него.
*/
export const RECORDABLE_ENFORCEMENT_EVENTS = Object.fromEntries(
  Object.entries(ENFORCEMENT_EVENT_TITLES).filter(([kind]) => kind !== 'cancelled'),
)

export const CHANNEL_TITLES = {
  bank: 'банк',
  bailiffs: 'судебный пристав',
  employer: 'работодатель',
}

export const STAGE_TITLES = {
  normal: 'в норме',
  pretrial: 'досудебная работа',
  filed: 'заявление подано',
  order_received: 'приказ получен',
  order_cancelled: 'приказ отменён',
  lawsuit: 'исковое производство',
  enforcement: 'исполнение',
  installment: 'рассрочка',
  excluded: 'исключён из взыскания',
}

/*
  Вкладка «Файлы»: сведения о счёте, которые приносят файлы, и где файл на пути
  к накопителю (30.09.2026). Первые четыре сведения - обязательный минимум,
  решение владельца; порядок показа задаёт сервер. Полноту обоих словарей
  сверяет с сервером backend/tests/unit/test_titles_match_enums.py.
*/
export const FACT_TITLES = {
  address: 'адрес',
  person: 'ФИО',
  ownership: 'вид собственности',
  charges: 'начисления по месяцам',
  paid: 'оплата по месяцам',
  due_dates: 'сроки оплаты',
  premises: 'сведения о помещении',
  contacts: 'телефон или почта',
  shares: 'доли',
  identity: 'паспорт, ИНН или СНИЛС',
  bankruptcy: 'банкротство',
  declared_debt: 'итог долга по расчёту УК',
  statement: 'поступления из выписки',
  resident: 'зарегистрированные в помещении',
}

/*
  Вид собственности помещения на карточке счёта (07.10.2026, решение владельца):
  счёт без вида заводится обменом, а вид выбирает человек. Слова - для экрана;
  в бланке финансово-лицевого счёта свои (account_statement_service), и правка
  одних не должна менять другие. Полноту сверяет test_titles_match_enums.py.
*/
export const PREMISES_TYPE_TITLES = {
  ownership: 'Частная собственность',
  social_tenancy: 'Муниципальная, социальный наём',
  public_property: 'Государственная или ведомственная',
}

export const UPLOAD_STATE_TITLES = {
  received: 'читается',
  read: 'разобран',
  needs_review: 'ждёт сверки',
  failed: 'не прочитан',
}

export const facts = (list) => list.map((fact) => FACT_TITLES[fact] ?? fact).join(', ')

/*
  Чем значение файла отличается от портфеля (01.10.2026, строка 178 реестра
  компромиссов): первые два - по существу, остальные - тот же дом и квартира,
  записанные иначе. Порядок показа задаёт сервер.
*/
export const DIFFERENCE_TITLES = {
  place: 'другой дом или квартира',
  ownership: 'другой вид',
  fuller: 'в файле полнее',
  written: 'записано иначе',
  shorter: 'в файле короче',
}

/* Размер файла для человека: «340 КБ», «2,4 МБ». Меньше мегабайта - в килобайтах. */
export function fileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('ru-RU')} КБ`
  return `${(bytes / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`
}
